import React, { useState } from 'react';
import { Calendar, Clock, Phone, Mail, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BookDemo = () => {
  const [form, setForm] = useState({ owner: '', first_name: '', last_name: '', email: '', phone: '', restaurant_name: '', postal_code: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { setError('Something went wrong. Please try again.'); setSubmitting(false); return; }
      setSubmitted(true);
    } catch { setError('Connection error. Please try again.'); setSubmitting(false); }
  };

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  if (submitted) {
    return (
      <div>
        <section className="halftone-bg" style={{ background: '#FFD700', borderBottom: '4px solid #000', padding: '5rem 1.5rem', textAlign: 'center', position: 'relative' }}>
          <div className="container" style={{ maxWidth: '800px', position: 'relative', zIndex: 1 }}>
            <h1 className="heading-1" style={{ marginBottom: '1.5rem', color: '#000' }}>Book a Free Demo</h1>
            <p style={{ fontSize: '1.25rem', color: '#333', fontWeight: 600 }}>See HungrMates in action with a personalized 30-minute demo.</p>
          </div>
        </section>
        <section style={{ padding: '5rem 1.5rem', background: '#fff', borderBottom: '4px solid #000' }}>
          <div className="container" style={{ maxWidth: '600px', textAlign: 'center' }}>
            <CheckCircle size={64} color="#22c55e" style={{ marginBottom: '1.5rem' }} />
            <h2 style={{ fontFamily: "'Bangers', cursive", fontSize: '2.5rem', color: '#22c55e', letterSpacing: '0.03em', marginBottom: '1rem' }}>Request Received!</h2>
            <p style={{ fontSize: '1.15rem', color: '#333', fontWeight: 600, marginBottom: '1.5rem' }}>
              Thanks {form.first_name}! We'll reach out shortly to schedule your free demo for <strong>{form.restaurant_name}</strong>.
            </p>
            <div style={{ padding: '1.25rem', background: '#FFF9DB', border: '4px solid #000', borderRadius: '12px', boxShadow: '6px 6px 0px 0px #000' }}>
              <p style={{ fontWeight: 700, marginBottom: '0.5rem' }}>In the meantime, feel free to reach us directly:</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
                <a href="tel:+14705260852" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6A0DAD', fontWeight: 700, textDecoration: 'none' }}>
                  <Phone size={18} /> (470) 526-0852
                </a>
                <a href="mailto:Mike@hungrmates.com" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6A0DAD', fontWeight: 700, textDecoration: 'none' }}>
                  <Mail size={18} /> Mike@hungrmates.com
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <section className="halftone-bg" style={{ background: '#FFD700', borderBottom: '4px solid #000', padding: '5rem 1.5rem', textAlign: 'center', position: 'relative' }}>
        <div className="container" style={{ maxWidth: '800px', position: 'relative', zIndex: 1 }}>
          <h1 className="heading-1" style={{ marginBottom: '1.5rem', color: '#000' }}>Book a Free Demo</h1>
          <p style={{ fontSize: '1.25rem', color: '#333', fontWeight: 600 }}>See HungrMates in action with a personalized 30-minute demo.</p>
        </div>
      </section>

      <section style={{ padding: '5rem 1.5rem', background: '#fff', borderBottom: '4px solid #000' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'start' }}>
            {/* Left: What to Expect */}
            <div>
              <h2 style={{ fontFamily: "'Bangers', cursive", fontSize: '2rem', marginBottom: '1.5rem', color: '#000', letterSpacing: '0.03em', textTransform: 'uppercase' }}>What to Expect</h2>
              {[
                { icon: <Calendar size={24} />, title: '30-Minute Demo', desc: 'Quick overview of all features' },
                { icon: <Clock size={24} />, title: 'Same-Day Setup', desc: 'Many restaurants go live in 2 weeks' },
                { icon: <Phone size={24} />, title: 'Personalized', desc: 'Tailored to your restaurant needs' },
                { icon: <Mail size={24} />, title: 'No Obligation', desc: 'Zero pressure, just information' },
              ].map((item, i) => (
                <div key={i} className="comic-card" style={{ marginBottom: '1rem', padding: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: '#FFD700', border: '3px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '3px 3px 0px 0px #000' }}>
                    {item.icon}
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.95rem' }}>{item.title}</div>
                    <div style={{ color: '#555', fontWeight: 500, fontSize: '0.9rem' }}>{item.desc}</div>
                  </div>
                </div>
              ))}

              <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#FFF9DB', border: '4px solid #000', borderRadius: '12px', boxShadow: '6px 6px 0px 0px #000' }}>
                <h3 style={{ fontWeight: 800, textTransform: 'uppercase', marginBottom: '0.75rem' }}>Contact Us Directly</h3>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <Phone size={18} color="#6A0DAD" />
                  <a href="tel:+14705260852" style={{ color: '#6A0DAD', fontWeight: 700, textDecoration: 'none' }}>(470) 526-0852</a>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <Mail size={18} color="#6A0DAD" />
                  <a href="mailto:Mike@hungrmates.com" style={{ color: '#6A0DAD', fontWeight: 700, textDecoration: 'none' }}>Mike@hungrmates.com</a>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="comic-card" style={{ padding: '2rem' }}>
              <h3 style={{ fontFamily: "'Bangers', cursive", fontSize: '1.75rem', marginBottom: '0.25rem', color: '#000', letterSpacing: '0.03em' }}>
                Profitable Online Ordering Starts Here
              </h3>
              <div style={{ height: '3px', background: '#FFD700', marginBottom: '1.5rem' }} />

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <div>
                  <label style={{ fontWeight: 700, fontSize: '0.9rem', color: '#333', marginBottom: '0.3rem', display: 'block' }}>
                    Do you own or operate a restaurant?<span style={{ color: '#FF3B30' }}>*</span>
                  </label>
                  <div style={{ display: 'flex', gap: '1.5rem', padding: '0.25rem 0' }}>
                    {['Yes', 'No'].map(v => (
                      <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 600, color: '#333' }}>
                        <input type="radio" name="owner" value={v} required checked={form.owner === v}
                          onChange={() => update('owner', v)}
                          style={{ width: '16px', height: '16px', accentColor: '#6A0DAD' }} />
                        {v}
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                  {[
                    { key: 'first_name', label: 'First Name', required: true },
                    { key: 'last_name', label: 'Last Name', required: true },
                    { key: 'email', label: 'Email Address', type: 'email', required: true, span: true },
                    { key: 'phone', label: 'Phone Number', type: 'tel', required: true },
                    { key: 'restaurant_name', label: 'Restaurant Name', required: true },
                    { key: 'postal_code', label: 'Postal Code', required: true, span: true },
                  ].map(f => (
                    <div key={f.key} style={f.span ? { gridColumn: '1 / -1' } : {}}>
                      <label style={{ fontWeight: 700, fontSize: '0.85rem', color: '#333', marginBottom: '0.25rem', display: 'block' }}>
                        {f.label}{f.required && <span style={{ color: '#FF3B30' }}>*</span>}
                      </label>
                      <input
                        data-testid={`demo-${f.key}`}
                        type={f.type || 'text'}
                        value={form[f.key]}
                        onChange={(e) => update(f.key, e.target.value)}
                        required={f.required}
                        placeholder={f.label}
                        style={{
                          width: '100%', padding: '0.6rem', background: '#f5f5f5', border: '2px solid #ddd',
                          borderRadius: '6px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box',
                          transition: 'border-color 0.2s',
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#6A0DAD'}
                        onBlur={(e) => e.target.style.borderColor = '#ddd'}
                      />
                    </div>
                  ))}
                </div>

                <p style={{ fontSize: '0.8rem', color: '#666', lineHeight: 1.5, margin: '0.25rem 0' }}>
                  We'll store and process this information to provide you our products and services. You may opt out of this at any time.
                </p>

                {error && <p style={{ color: '#FF3B30', fontWeight: 700, fontSize: '0.9rem' }}>{error}</p>}

                <button
                  data-testid="demo-submit-btn"
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '1rem', background: '#000', color: '#fff',
                    border: '3px solid #000', borderRadius: '50px', fontWeight: 800,
                    fontSize: '1.1rem', cursor: submitting ? 'wait' : 'pointer',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    transition: 'all 0.15s', opacity: submitting ? 0.7 : 1,
                  }}
                >
                  {submitting ? 'Submitting...' : 'Book a Demo'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          div[style*="gridTemplateColumns"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};
